/**
 * TikTokSettingsDialog — the COMPLIANT per-post Direct Post dialog (TikTok Content
 * Sharing Guidelines). Shown every time the user taps "Post to TikTok"; it is NOT a
 * saved-defaults screen. It implements the required UX:
 *   • shows which account is being posted to (avatar + @handle from creator_info),
 *   • caption,
 *   • WHO CAN VIEW — privacy level the user must ACTIVELY select (nothing pre-selected;
 *     options come from creator_info.privacy_level_options; Post is disabled until chosen),
 *   • interaction toggles (comment / duet / stitch) that reflect AND LOCK the creator's
 *     own TikTok settings,
 *   • commercial-content disclosure (Your brand / Branded content) with TikTok's EXACT
 *     required copy; branded content cannot be private,
 *   • the required compliance declaration (Music Usage Confirmation, + Branded Content
 *     Policy when disclosed).
 * The disclosure/compliance strings are TikTok's exact wording — do NOT paraphrase.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/v2/utils';
import { BrandIcon, BRAND } from '@/v2/components/BrandIcons';
import { getTikTokCreatorInfo, type TikTokCreatorInfo, type TikTokPostInfo } from '@/v2/social';

const YOUR_BRAND_HINT = 'You are promoting yourself or your own business. This video will be classified as Brand Organic.';
const BRANDED_HINT = 'You are promoting another brand or a third party. This video will be classified as Branded Content.';
const DISCLOSURE_HINT =
  'Turn on to disclose that this video promotes goods or services in exchange for something of value. You may need to add music to your video.';
const MUSIC_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en';
const BRANDED_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en';
const PRIVACY_LABEL: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Everyone', MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers', SELF_ONLY: 'Only me',
};

function Toggle({ on, disabled, onClick }: { on: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} disabled={disabled} onClick={onClick}
      className={cn('relative w-10 h-6 rounded-full transition-colors shrink-0',
        on ? 'bg-primary' : 'bg-surface-light', disabled && 'opacity-40 cursor-not-allowed')}
    >
      <span className={cn('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform', on && 'translate-x-4')} />
    </button>
  );
}

export function TikTokSettingsDialog({
  initialCaption,
  onPost,
  onClose,
}: {
  initialCaption: string;
  onPost: (info: TikTokPostInfo) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('v2');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [info, setInfo] = useState<TikTokCreatorInfo | null>(null);

  const [caption, setCaption] = useState(initialCaption);
  // Privacy starts UNSELECTED — TikTok requires the user to actively choose it every post.
  const [privacy, setPrivacy] = useState('');
  const [allowComments, setAllowComments] = useState(true);
  const [allowDuet, setAllowDuet] = useState(true);
  const [allowStitch, setAllowStitch] = useState(true);
  const [disclose, setDisclose] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);

  useEffect(() => {
    let alive = true;
    getTikTokCreatorInfo()
      .then((ci) => {
        if (!alive) return;
        setInfo(ci);
        // Interaction toggles default to (and lock at) the creator's account settings.
        if (ci.comment_disabled) setAllowComments(false);
        if (ci.duet_disabled) setAllowDuet(false);
        if (ci.stitch_disabled) setAllowStitch(false);
        // Privacy intentionally left unselected — the user must pick it.
      })
      .catch((e) => { if (alive) setLoadError(e instanceof Error ? e.message : 'failed'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Branded content can't be private — hide SELF_ONLY and clear it if it was chosen.
  const privacyOptions = useMemo(() => {
    const opts = info?.privacy_level_options || [];
    return brandedContent ? opts.filter((o) => o !== 'SELF_ONLY') : opts;
  }, [info, brandedContent]);
  useEffect(() => {
    if (brandedContent && privacy === 'SELF_ONLY') setPrivacy('');
  }, [brandedContent, privacy]);

  const discloseIncomplete = disclose && !yourBrand && !brandedContent;
  const canPost = !!privacy && !discloseIncomplete;

  const post = () => {
    if (!canPost) return;
    onPost({
      caption: caption.trim(),
      privacy_level: privacy,
      disable_comment: !allowComments,
      disable_duet: !allowDuet,
      disable_stitch: !allowStitch,
      brand_organic_toggle: disclose && yourBrand,
      brand_content_toggle: disclose && brandedContent,
    });
  };

  const handle = info?.creator_username ? `@${info.creator_username}` : (info?.creator_nickname || 'TikTok');

  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl bg-[#15151d] border border-white/10 shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-white/10 sticky top-0 bg-[#15151d] z-10">
          {info?.creator_avatar_url ? (
            <img src={info.creator_avatar_url} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
          ) : (
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: BRAND.tiktok.color }}>
              <BrandIcon provider="tiktok" className="w-4 h-4 text-white" />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-text-primary">{t('tiktokPost.title')}</h2>
            <p className="text-xs text-text-tertiary truncate">{t('tiktokPost.postingAs', { handle })}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-text-tertiary"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-text-tertiary"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : loadError ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-7 h-7 text-amber-400 mx-auto mb-3" />
            <p className="text-sm text-text-secondary mb-4">{loadError === 'reconnect_required' ? t('tiktokPost.reconnect') : t('tiktokPost.loadFailed')}</p>
            <button onClick={onClose} className="px-4 py-2 rounded-full glass text-sm font-semibold text-text-primary">{t('tiktokPost.close')}</button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">{t('tiktokPost.caption')}</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 2200))}
                rows={4}
                placeholder={t('tiktokPost.captionPlaceholder')}
                className="w-full bg-surface-light border border-glass-border rounded-xl px-3.5 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 resize-none"
              />
              <div className="mt-1 text-right text-[11px] text-text-tertiary tabular-nums">{caption.length}/2200</div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">{t('tiktokPost.whoCanView')}</label>
              <div className="space-y-1.5">
                {privacyOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setPrivacy(opt)}
                    className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left text-sm transition-colors',
                      privacy === opt ? 'border-primary bg-primary/10 text-text-primary' : 'border-glass-border bg-surface hover:border-primary/30 text-text-secondary')}
                  >
                    <span className={cn('w-4 h-4 rounded-full border-2 shrink-0 grid place-items-center', privacy === opt ? 'border-primary' : 'border-text-tertiary')}>
                      {privacy === opt && <span className="w-2 h-2 rounded-full bg-primary" />}
                    </span>
                    {t(`tiktokPost.privacy.${opt}`, { defaultValue: PRIVACY_LABEL[opt] || opt })}
                  </button>
                ))}
              </div>
              {!privacy && <p className="text-[11px] text-amber-400 mt-1.5 px-1">{t('tiktokPost.pickPrivacy')}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide">{t('tiktokPost.allowUsersTo')}</label>
              {([
                ['comment', allowComments, setAllowComments, info?.comment_disabled],
                ['duet', allowDuet, setAllowDuet, info?.duet_disabled],
                ['stitch', allowStitch, setAllowStitch, info?.stitch_disabled],
              ] as const).map(([key, val, set, disabled]) => (
                <div key={key} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-surface border border-glass-border">
                  <span className="flex-1 text-sm text-text-primary">{t(`tiktokPost.${key}`)}</span>
                  <Toggle on={!disabled && val} disabled={!!disabled} onClick={() => set((v) => !v)} />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-surface border border-glass-border">
                <span className="flex-1">
                  <span className="block text-sm font-medium text-text-primary">{t('tiktokPost.discloseTitle')}</span>
                  <span className="block text-[11px] text-text-tertiary mt-0.5">{DISCLOSURE_HINT}</span>
                </span>
                <Toggle on={disclose} onClick={() => { setDisclose((v) => !v); if (disclose) { setYourBrand(false); setBrandedContent(false); } }} />
              </div>
              {disclose && (
                <div className="space-y-1.5 pl-1">
                  <button type="button" onClick={() => setYourBrand((v) => !v)} className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl border border-glass-border bg-surface text-left">
                    <span className={cn('w-4 h-4 rounded border-2 shrink-0 grid place-items-center mt-0.5', yourBrand ? 'border-primary bg-primary' : 'border-text-tertiary')}>{yourBrand && <Check className="w-3 h-3 text-white" />}</span>
                    <span><span className="block text-sm text-text-primary">{t('tiktokPost.yourBrand')}</span><span className="block text-[11px] text-text-tertiary mt-0.5">{YOUR_BRAND_HINT}</span></span>
                  </button>
                  <button type="button" onClick={() => setBrandedContent((v) => !v)} className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl border border-glass-border bg-surface text-left">
                    <span className={cn('w-4 h-4 rounded border-2 shrink-0 grid place-items-center mt-0.5', brandedContent ? 'border-primary bg-primary' : 'border-text-tertiary')}>{brandedContent && <Check className="w-3 h-3 text-white" />}</span>
                    <span><span className="block text-sm text-text-primary">{t('tiktokPost.brandedContent')}</span><span className="block text-[11px] text-text-tertiary mt-0.5">{BRANDED_HINT}</span></span>
                  </button>
                  {discloseIncomplete && <p className="text-[11px] text-amber-400 px-1">{t('tiktokPost.discloseRequired')}</p>}
                  {brandedContent && <p className="text-[11px] text-text-tertiary px-1">{t('tiktokPost.brandedPrivacyNote')}</p>}
                </div>
              )}
            </div>

            <button
              onClick={post}
              disabled={!canPost}
              className="w-full py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('tiktokPost.postNow')}
            </button>

            {/* TikTok's required compliance declaration (exact wording — do not paraphrase). */}
            <p className="text-[11px] text-text-tertiary text-center leading-relaxed px-2 -mt-1">
              By posting, you agree to TikTok's{' '}
              {(disclose && brandedContent) && (
                <>
                  <a href={BRANDED_URL} target="_blank" rel="noreferrer" className="underline hover:text-text-secondary">Branded Content Policy</a>
                  {' and '}
                </>
              )}
              <a href={MUSIC_URL} target="_blank" rel="noreferrer" className="underline hover:text-text-secondary">Music Usage Confirmation</a>.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
